import type { Lang, LorsCopy } from "./translations";

export type Sep22Headline = { slug: string; headline: string; subhead: string };

// Headline variants for h=1..15 driven by the ad angle (probate cost/family protection).
export const sep22Headlines: Sep22Headline[] = [
  {
    slug: "h1",
    headline: "You Built the Life — Now Make Sure Your Family Doesn't Lose Months in Court Claiming It",
    subhead: "Join attorney Ruby Steinbrecher on September 22nd for a one-hour workshop on wills, trusts, and probate in Santa Rosa.",
  },
  {
    slug: "h2",
    headline: "Good Intentions Don't Skip Probate — But the Right Plan Does",
    subhead: "Free workshop September 22nd at the Finley Community Center. Learn how California families use trusts to avoid probate court.",
  },
  {
    slug: "h3",
    headline: "Six Hidden Costs of Probate That Shrink What Your Family Inherits",
    subhead: "Attend the free September 22nd workshop and leave with a clear picture of probate fees, delays, and how to prevent them.",
  },
  {
    slug: "h4",
    headline: "Probate Fees on a Sonoma County Home Can Hit Five Figures — Here's Why",
    subhead: "Ruby Steinbrecher breaks down California probate costs and how a funded trust can protect your home. September 22nd, Santa Rosa.",
  },
  {
    slug: "h5",
    headline: "Love Isn't a Plan — Give Your Family a Roadmap, Not a Riddle",
    subhead: "A free estate planning workshop on September 22nd. Get clear answers on wills, trusts, and powers of attorney in California.",
  },
  {
    slug: "h6",
    headline: "See the Full Picture: What Probate Really Costs Sonoma County Families",
    subhead: "Join us September 22nd at the Finley Community Center for an educational session on avoiding probate and preserving your legacy.",
  },
  {
    slug: "h7",
    headline: "Here's How California Families Are Avoiding Probate (It’s Slow, Expensive and Public)",
    subhead: "Free September 22nd workshop in Santa Rosa. Learn the practical steps families take to transfer assets without court involvement.",
  },
  {
    slug: "h8",
    headline: "What a Will Can't Do for Your Family — and What a Trust Can",
    subhead: "Attorney Ruby Steinbrecher explains the difference between wills and living trusts. Free workshop, September 22nd in Santa Rosa.",
  },
  {
    slug: "h9",
    headline: "Wills, Trusts, or Both? Get Clear Answers in One Hour",
    subhead: "Reserve your seat for the free September 22nd estate planning workshop at the Finley Community Center.",
  },
  {
    slug: "h10",
    headline: "Grief Is Hard Enough Without Adding Paperwork, Deadlines, and Court Dates",
    subhead: "Join attorney Ruby Steinbrecher on September 22nd and learn how to make your estate plan easier on the people you love most.",
  },
  {
    slug: "h11",
    headline: "The Most Expensive Mistake in California? Thinking a Will Is Enough",
    subhead: "Find out why a will alone may still send your family through probate. Free workshop September 22nd in Santa Rosa.",
  },
  {
    slug: "h12",
    headline: "Your Home Has a Lock on the Door — Make Sure It Has One on the Title Too",
    subhead: "Free September 22nd workshop on protecting real estate and other assets with a California estate plan.",
  },
  {
    slug: "h13",
    headline: "One Workshop Now Could Save Your Family a Year in Court",
    subhead: "Join us September 22nd at the Finley Community Center for a practical, no-pressure estate planning session.",
  },
  {
    slug: "h14",
    headline: "You Chose Every Detail of Your Home — Don't Let a Court Decide Who Keeps It",
    subhead: "Attorney Ruby Steinbrecher covers wills, trusts, and probate avoidance. Free September 22nd workshop in Santa Rosa.",
  },
  {
    slug: "h15",
    headline: "Your Family Shouldn't Have to Google Their Way Through Grief",
    subhead: "Get clear, practical estate planning guidance at the free September 22nd workshop. Limited seating available.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Workshop",
  headline: sep22Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: sep22Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What You'll Learn at This Workshop",
  learnItems: [
    "How wills, living trusts, and powers of attorney each work in California",
    "How a properly funded living trust can help a family pass assets without probate court",
    "What California's probate process generally looks like — timelines, court involvement, and typical costs",
    "Practical first steps for putting an estate plan in place, and the questions to ask before you do",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Tue, September 22nd", long: "Tuesday, September 22nd" },
  formTime: "4:00–5:00 PM",
  formVenueLine1: "Finley Community Center — Cypress Room",
  formVenueLine2: "2060 West College Ave, Santa Rosa, CA 95401",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "",
  getDirections: "Get Directions",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey's bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000's.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Save Your Seat?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make an informed decision.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentSep22 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const FINLEY_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Finley+Community+Center,+2060+West+College+Ave,+Santa+Rosa,+CA+95401";

export const getSep22Copy = (hParam: string | null): LorsCopy => {
  let idx = 0;
  if (hParam) {
    const slugIdx = sep22Headlines.findIndex((h) => h.slug === hParam);
    if (slugIdx >= 0) {
      idx = slugIdx;
    } else {
      const n = Number(hParam);
      if (Number.isInteger(n) && n >= 1 && n <= sep22Headlines.length) {
        idx = n - 1;
      }
    }
  }
  const pick = sep22Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
